import httpClient from "../http/http-client";
import { IContactSupplierDetails, IPostContactSupplier, ILookupActivityPreview, ILookupActivityApplyItem, ILookupActivityByNameResult } from './contact-supplier.model';
import { ICompanyHouseSearchResult } from '../company-house/company-house.model';

export default class ContactSupplierService {
  http;
  suppliers?: IContactSupplierDetails[];
  activeSuppliers?: IContactSupplierDetails[];
  suppliersLite?: IContactSupplierDetails[];
  activeSuppliersLite?: IContactSupplierDetails[];
  // Keyed by factor year. Holds the request itself, so callers asking for a year that is still
  // loading share one request.
  private updatedSpendBasedSuppliers = new Map<string, Promise<IContactSupplierDetails[]>>();
  private suppliersLiteRequest?: Promise<IContactSupplierDetails[]>;
  // Bumped by every cache clear, so a response for a request made before the clear (another
  // organisation's, after a client switch) is returned to its caller but never cached.
  private cacheGeneration = 0;

  static instance = new ContactSupplierService();

  static getInstance() {
    if (ContactSupplierService.instance === null) ContactSupplierService.instance = new ContactSupplierService();
    return this.instance;
  }

  constructor() {
    this.http = httpClient
  }

  async getAllSuppliers(forceUpdate: boolean = false, queryString?: string): Promise<IContactSupplierDetails[]> {
    if (this.suppliers && !forceUpdate) {
      return Promise.resolve(this.suppliers);
    } else {
      return this.http.get(`contact-supplier${queryString ? queryString : ''}`)
        .then(response => {
          this.suppliers = response.data.rows;
          this.activeSuppliers = response.data.rows.filter((supplier: IContactSupplierDetails) => supplier.active);
          return response.data.rows;
        })
        .catch(err => {
          console.log(err);
          throw err;
        })
    }
  }

  async getAllActiveSuppliers(forceUpdate: boolean = false): Promise<IContactSupplierDetails[]> {
    if (this.activeSuppliers && !forceUpdate) {
      return Promise.resolve(this.activeSuppliers);
    } else {
      return this.http.get('contact-supplier')
        .then(response => {
          this.suppliers = response.data.rows;
          this.activeSuppliers = response.data.rows.filter((supplier: IContactSupplierDetails) => supplier.active);
          return response.data.rows.filter((supplier: IContactSupplierDetails) => supplier.active);
        })
        .catch(err => {
          console.log(err);
          throw err;
        })
    }
  }

  // The lite list: id, name, active, reference, default activity (spendBasedConversionId), default
  // spend source and primary contact email, which is all the supplier pickers and name lookups read.
  // Active and inactive, ordered by name. Use the full-record methods above only where the whole
  // supplier (with its spend-based conversion) is needed, i.e. the supplier pages.
  async getAllSuppliersLite(forceUpdate: boolean = false): Promise<IContactSupplierDetails[]> {
    if (this.suppliersLite && !forceUpdate) {
      return Promise.resolve(this.suppliersLite);
    }
    // One request serves every picker that asks while it is in flight (the breadcrumb and the
    // page both load suppliers on first render).
    if (this.suppliersLiteRequest && !forceUpdate) {
      return this.suppliersLiteRequest;
    }
    const generation = this.cacheGeneration;
    const request: Promise<IContactSupplierDetails[]> = this.http.get('contact-supplier?lite=true')
      .then(response => {
        const rows = response.data.rows as IContactSupplierDetails[];
        if (generation === this.cacheGeneration) {
          this.suppliersLite = rows;
          this.activeSuppliersLite = rows.filter((supplier) => supplier.active);
        }
        return rows;
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
      .finally(() => {
        if (this.suppliersLiteRequest === request) this.suppliersLiteRequest = undefined;
      });
    this.suppliersLiteRequest = request;
    return request;
  }

  // The lite list, active suppliers only. For pickers.
  async getActiveSuppliersLite(forceUpdate: boolean = false): Promise<IContactSupplierDetails[]> {
    if (this.activeSuppliersLite && !forceUpdate) {
      return Promise.resolve(this.activeSuppliersLite);
    }
    return this.getAllSuppliersLite(forceUpdate)
      .then(rows => this.activeSuppliersLite ?? rows.filter((supplier) => supplier.active));
  }

  // Active suppliers (lite fields) with spendBasedConversionId moved to each default activity's
  // closest factor for `year`. Cached per year.
  async getAllActiveSuppliersWithUpdatedSpendBasedConversions(year: string): Promise<IContactSupplierDetails[]> {
    const cached = this.updatedSpendBasedSuppliers.get(year);
    if (cached) {
      return cached;
    }
    const request: Promise<IContactSupplierDetails[]> = this.http.get(`contact-supplier/updated-spend-based-conversions?year=${year}`)
      .then(response => response.data)
      .catch(err => {
        // Not cached, so the next caller retries.
        if (this.updatedSpendBasedSuppliers.get(year) === request) this.updatedSpendBasedSuppliers.delete(year);
        console.log(err);
        throw err;
      });
    this.updatedSpendBasedSuppliers.set(year, request);
    return request;
  }

  // includeTotals adds each supplier's period totals (totalEmissions, spendTotals) for reportPeriodId.
  async getAllSuppliersWithPagination(active: boolean, percentangeActualData: boolean, reportPeriodId: string | null, searchQuery: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', noFactor?: boolean, includeTotals?: boolean): Promise<{ count: number, rows: IContactSupplierDetails[] }> {
    return this.http.get(`contact-supplier?active=${active}&percentangeActualData=${percentangeActualData}&reportPeriodId=${reportPeriodId}&page=${page}&perPage=${perPage}&nameIncludes=${searchQuery}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}${noFactor ? '&noFactor=true' : ''}${includeTotals && reportPeriodId ? '&includeTotals=true' : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  // Preview the Companies House lookup for suppliers with no default activity (read-only). Omit
  // supplierIds to preview every unclassified supplier for the org.
  async previewLookupDefaultActivity(supplierIds?: string[]): Promise<ILookupActivityPreview> {
    return this.http.post(`contact-supplier/lookup-default-activity/preview`, supplierIds ? { supplierIds } : {})
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  // Apply confirmed default-activity suggestions. Only fills blanks; never overwrites a factor.
  async applyLookupDefaultActivity(items: ILookupActivityApplyItem[]): Promise<{ applied: number, skipped: number }> {
    return this.http.post(`contact-supplier/lookup-default-activity/apply`, { items })
      .then(response => {
        this.clearPickerCaches();
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  // Look up a suggested default activity for a single free-text name (per-supplier form button).
  async previewLookupActivityByName(name: string): Promise<ILookupActivityByNameResult> {
    return this.http.post(`contact-supplier/lookup-default-activity/preview-name`, { name })
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  // Manual Companies House search for the supplier form picker.
  async searchCompanyHouse(term: string): Promise<ICompanyHouseSearchResult[]> {
    return this.http.get(`contact-supplier/company-house-search?term=${encodeURIComponent(term)}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // Resolve a manually-picked Companies House company to a default activity + provenance.
  async previewLookupActivityByCompany(company: { companyNumber: string, sicCode: string | null, companyName: string }): Promise<ILookupActivityByNameResult> {
    return this.http.post(`contact-supplier/lookup-default-activity/preview-company`, company)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getOneSupplier(id: string, includeUpdatedSpendBasedConversions: boolean = false): Promise<IContactSupplierDetails> {
    return this.http.get(`contact-supplier/${id}${includeUpdatedSpendBasedConversions ? '?includeUpdatedSpendBasedConversions=true' : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async createSupplier(newSupplier: IPostContactSupplier): Promise<IPostContactSupplier> {
    return this.http.post(`contact-supplier`, newSupplier)
      .then(response => {
        this.clearPickerCaches();
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }


  async updateSupplier(id: string, newSupplier: IPostContactSupplier): Promise<IPostContactSupplier> {
    return this.http.patch(`contact-supplier/${id}`, newSupplier)
      .then(response => {
        this.clearPickerCaches();
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async delete(id: string): Promise<IContactSupplierDetails> {
    return this.http.delete(`contact-supplier/${id}`)
      .then(response => {
        this.clearPickerCaches();
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  async makeSupplierInactive(id: string): Promise<IContactSupplierDetails> {
    return this.http.post(`contact-supplier/inactive`, { contactSupplierId: id })
      .then(response => {
        this.clearPickerCaches();
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      })
  }

  // The lite and per-year lists the pickers read. Cleared whenever a supplier is changed through
  // this service, because the pickers used to read the full cache, which the supplier pages refresh
  // after a save.
  private clearPickerCaches() {
    this.cacheGeneration += 1;
    this.suppliersLite = undefined;
    this.activeSuppliersLite = undefined;
    this.suppliersLiteRequest = undefined;
    this.updatedSpendBasedSuppliers.clear();
  }

  clearData() {
    this.suppliers = undefined;
    this.activeSuppliers = undefined;
    this.clearPickerCaches();
  }
}